import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { listScenarios, renewEditing } from '../api/scenarioApi';
import { StorageKey } from '../auth/DataService';

export interface ActiveScenario {
  id: string;
  name: string;
  mode: 'VIEW' | 'EDITING';
}

interface ScenarioContextValue {
  activeScenario: ActiveScenario | null;
  setActiveScenario: (scenario: ActiveScenario) => void;
  clearScenario: () => void;
  editingTimerReset: number;
  resetEditingTimer: () => void;
}

const ScenarioContext = createContext<ScenarioContextValue | null>(null);

const ScenarioProvider = ({ children }: PropsWithChildren) => {
  const [activeScenario, setActiveScenarioState] =
    useState<ActiveScenario | null>(null);
  const [editingTimerReset, setEditingTimerReset] = useState(0);

  useEffect(() => {
    const currentUser = localStorage.getItem(StorageKey.userInfo_sub) ?? '';
    listScenarios()
      .then((scenarios) => {
        const isViewOrEditing = (status: string) =>
          status === 'VIEW' || status === 'EDITING';

        const owned = (scenarios ?? []).find(
          (s) => isViewOrEditing(s.status) && s.createdBy === currentUser
        );
        if (owned) {
          const mode = owned.status === 'EDITING' ? 'EDITING' : 'VIEW';
          setActiveScenarioState({ id: owned.id, name: owned.name, mode });
          if (mode === 'EDITING') {
            renewEditing(owned.id).catch(() => {});
            setEditingTimerReset((c) => c + 1);
          }
          return;
        }

        const shared = (scenarios ?? []).find(
          (s) =>
            s.createdBy !== currentUser &&
            (s.scenarioSharedUsers ?? []).some(
              (su) =>
                su.sharedUser.emea === currentUser && isViewOrEditing(su.status)
            )
        );
        if (shared) {
          const sharedEntry = (shared.scenarioSharedUsers ?? []).find(
            (su) =>
              su.sharedUser.emea === currentUser && isViewOrEditing(su.status)
          );
          const mode = sharedEntry?.status === 'EDITING' ? 'EDITING' : 'VIEW';
          setActiveScenarioState({
            id: shared.id,
            name: shared.name,
            mode,
          });
          if (mode === 'EDITING') {
            renewEditing(shared.id).catch(() => {});
            setEditingTimerReset((c) => c + 1);
          }
        }
      })
      .catch(() => {});
  }, []);

  const setActiveScenario = useCallback((scenario: ActiveScenario) => {
    setActiveScenarioState(scenario);
    if (scenario.mode === 'EDITING') {
      setEditingTimerReset((c) => c + 1);
    }
  }, []);

  const clearScenario = useCallback(() => {
    setActiveScenarioState(null);
  }, []);

  const resetEditingTimer = useCallback(() => {
    setEditingTimerReset((c) => c + 1);
  }, []);

  return (
    <ScenarioContext.Provider
      value={{
        activeScenario,
        setActiveScenario,
        clearScenario,
        editingTimerReset,
        resetEditingTimer,
      }}
    >
      {children}
    </ScenarioContext.Provider>
  );
};

const useScenario = (): ScenarioContextValue => {
  const ctx = useContext(ScenarioContext);
  if (ctx === null) {
    throw new Error('useScenario must be used within a <ScenarioProvider>');
  }
  return ctx;
};

export { ScenarioProvider, useScenario };
